/**
 * Scrydex API Types
 * 
 * Type definitions for the Scrydex pricing API data structures.
 * Used for both API responses and database storage.
 */

// =====================================================
// TREND DATA TYPES
// =====================================================

export interface PriceTrend {
  price_change: number;
  percent_change: number;
}

export interface PriceTrends {
  days_1?: PriceTrend;
  days_7?: PriceTrend;
  days_14?: PriceTrend;
  days_30?: PriceTrend;
  days_90?: PriceTrend;
  days_180?: PriceTrend;
}

// =====================================================
// RAW PRICE TYPES
// =====================================================

export type RawCondition = 'NM' | 'LP' | 'MP' | 'HP' | 'DM';

export interface ScrydexRawPrice {
  condition: RawCondition;
  finish: string;
  is_perfect: boolean;
  is_signed: boolean;
  is_error: boolean;
  type: 'raw';
  low: number;
  market: number;
  currency: string;
  trends: PriceTrends;
}

// =====================================================
// GRADED PRICE TYPES
// =====================================================

export type GradingCompany = 'PSA' | 'CGC' | 'BGS' | 'TAG' | 'SGC' | 'OTHER';

export interface ScrydexGradedPrice {
  grade: string;
  company: GradingCompany;
  finish: string;
  is_perfect: boolean;
  is_signed: boolean;
  is_error: boolean;
  type: 'graded';
  low: number;
  mid: number;
  high: number;
  market: number;
  currency: string;
  trends: PriceTrends;
}

// =====================================================
// API RESPONSE TYPES
// =====================================================

export interface ScrydexCardPricesResponse {
  card_id: string;
  raw_prices: ScrydexRawPrice[];
  graded_prices: ScrydexGradedPrice[];
}

// =====================================================
// DATABASE TYPES
// =====================================================

export interface DbScrydexRawPrice {
  id: string;
  card_id: string;
  condition: RawCondition;
  finish: string;
  low_price: number | null;
  market_price: number | null;
  currency: string;
  trends: PriceTrends | null;
  fetched_at: string;
  created_at: string;
  updated_at: string;
}

export interface DbScrydexGradedPrice {
  id: string;
  card_id: string;
  grading_company: GradingCompany;
  grade: string;
  finish: string;
  is_perfect: boolean;
  is_signed: boolean;
  is_error: boolean;
  low_price: number | null;
  mid_price: number | null;
  high_price: number | null;
  market_price: number | null;
  currency: string;
  trends: PriceTrends | null;
  fetched_at: string;
  created_at: string;
  updated_at: string;
}

export interface DbPriceHistoryEntry {
  id: string;
  card_id: string;
  price_type: string;
  low_price: number | null;
  market_price: number | null;
  high_price: number | null;
  price_date: string;
  created_at: string;
}

// =====================================================
// COMBINED PRICING DATA (for frontend)
// =====================================================

export interface CardPricingData {
  cardId: string;
  raw: {
    condition: RawCondition;
    finish: string;
    low: number | null;
    market: number | null;
    trends: PriceTrends | null;
    updatedAt: string;
  }[];
  graded: {
    company: GradingCompany;
    grade: string;
    finish: string;
    isPerfect: boolean;
    isSigned: boolean;
    isError: boolean;
    low: number | null;
    mid: number | null;
    high: number | null;
    market: number | null;
    trends: PriceTrends | null;
    updatedAt: string;
  }[];
}

export interface PriceHistoryPoint {
  date: string;
  low: number | null;
  market: number | null;
  high: number | null;
}

// =====================================================
// CHART DATA TYPES
// =====================================================

export interface ChartDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface PriceChartData {
  priceType: string;
  data: ChartDataPoint[];
  currentPrice: number;
  priceChange: number;
  percentChange: number;
}

// =====================================================
// CONDITION LABELS
// =====================================================

export const CONDITION_LABELS: Record<RawCondition, string> = {
  NM: 'Near Mint',
  LP: 'Lightly Played',
  MP: 'Moderately Played',
  HP: 'Heavily Played',
  DM: 'Damaged'
};

export const GRADING_COMPANY_LABELS: Record<GradingCompany, string> = {
  PSA: 'PSA (Professional Sports Authenticator)',
  CGC: 'CGC (Certified Guaranty Company)',
  BGS: 'BGS (Beckett Grading Services)',
  TAG: 'TAG',
  SGC: 'SGC',
  OTHER: 'Other'
};

export const TREND_PERIOD_LABELS: Record<string, string> = {
  days_1: '24 Hours',
  days_7: '7 Days',
  days_14: '14 Days',
  days_30: '30 Days',
  days_90: '90 Days',
  days_180: '180 Days'
};

export const FINISH_LABELS: Record<string, string> = {
  normal: 'Normal',
  holofoil: 'Holofoil',
  reverseHolofoil: 'Reverse Holofoil',
  pokeBallReverseHolofoil: 'Poke Ball Reverse Holofoil',
  masterBallReverseHolofoil: 'Master Ball Reverse Holofoil',
  energyReverseHolofoil: 'Energy Reverse Holofoil',
};

export function getFinishLabel(finish: string): string {
  if (FINISH_LABELS[finish]) return FINISH_LABELS[finish];
  return finish
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

